import React, { useState, useEffect } from "react";
import {
  Show,
  TabbedShowLayout,
  Datagrid,
  TextField,
  useTranslate,
  FunctionField,
  ArrayField,
  BooleanField,
  ReferenceField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  Filter,
  NumberInput,
  TextInput,
  NumberField,
  useRecordContext,
  SelectInput,
} from "react-admin";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { JsonField } from "react-admin-json-view";
import {
  DateField,
  Title,
  ShowOnlyTopToolbar,
  EmptyMessage,
} from "../../../shared/components/index.js";
import { useTheme } from "@mui/material/styles/index.js";

const DatexTransactionFilter = ({ setSum, setAmount }) => (
  <Filter>
    <TextInput
      source="issuerName"
      label="resources.Client.fields.petrolcard_transactions.issuerName"
      alwaysOn
      resettable={true}
    />
    <NumberInput
      source="amount"
      label="resources.Client.fields.petrolcard_transactions.amount"
      alwaysOn
      onChange={(e) => setAmount(e.target.value)}
      resettable="true"
    />
    <NumberInput
      source="sum"
      label="resources.Client.fields.petrolcard_transactions.sum"
      onChange={(e) => setSum(e.target.value)}
      alwaysOn
      resettable="true"
    />
    <SelectInput
      alwaysOn
      resettable
      source="status"
      choices={[
        { id: "PROCESSED", name: "PROCESSED" },
        { id: "FAILED", name: "FAILED" },
      ]}
      optionText="name"
      optionValue="id"
    />
  </Filter>
);

DatexTransactionFilter.propTypes = {
  setSum: PropTypes.func,
  setAmount: PropTypes.func,
};

const FilteredTransactions = ({ data }) => {
  if (!data || data.length === 0) {
    return <EmptyMessage />;
  }

  return (
    <Datagrid
      empty={<EmptyMessage />}
      rowClick={false}
      bulkActionButtons={false}
      data={data}
      total={data.length}
    >
      <TextField
        source="accountName"
        label="resources.Client.fields.petrolcard_transactions.accountName"
      />
      <NumberField
        source="cardId"
        label="resources.Client.fields.petrolcard_transactions.cardId"
      />
      <TextField
        source="serialVisible"
        label="resources.Client.fields.petrolcard_transactions.serialVisible"
      />
      <NumberField
        source="price"
        label="resources.Client.fields.petrolcard_transactions.price"
      />
      <NumberField
        source="amount"
        label="resources.Client.fields.petrolcard_transactions.amount"
      />
      <NumberField
        source="sum"
        label="resources.Client.fields.petrolcard_transactions.sum"
      />
      <NumberField
        source="politicsDiscountId"
        label="resources.Client.fields.petrolcard_transactions.politicsDiscountId"
      />
      <NumberField
        source="discountValue"
        label="resources.Client.fields.petrolcard_transactions.discountValue"
      />
      <NumberField
        source="versionId"
        label="resources.Client.fields.petrolcard_transactions.versionId"
      />
      <NumberField
        source="transactionId"
        label="resources.Client.fields.petrolcard_transactions.transactionId"
      />
      <TextField
        source="issuerName"
        label="resources.Client.fields.petrolcard_transactions.issuerName"
      />
      <TextField
        source="tradePointName"
        label="resources.Client.fields.petrolcard_transactions.tradePointName"
      />
      <TextField
        source="tradePointAddress"
        label="resources.Client.fields.petrolcard_transactions.tradePointAddress"
      />
      <TextField source="status" label="resources.Client.fields.status" />
      <FunctionField
        source="transactionTime"
        label="resources.Client.fields.petrolcard_transactions.transactionTime"
        render={(tx) =>
          tx?.transactionTime
            ? moment(tx.transactionTime).format("DD.MM.YYYY HH:mm:ss")
            : ""
        }
      />
    </Datagrid>
  );
};

FilteredTransactions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const TabbedShowLayoutTabs = () => {
  const theme = useTheme();
  const [sum, setSum] = useState(null);
  const [amount, setAmount] = useState(null);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const t = useTranslate();
  const record = useRecordContext();
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    if (record && record.transactions) {
      let filtered = [...record.transactions];

      if (sum !== null && sum !== undefined && sum !== "") {
        const parsedSum = parseFloat(sum);

        if (!isNaN(parsedSum)) {
          filtered = filtered.filter(
            (transaction) => parseFloat(transaction.sum) >= parsedSum
          );
        }
      }

      if (amount !== null && amount !== undefined && amount !== "") {
        const parsedAmount = parseFloat(amount);

        if (!isNaN(parsedAmount)) {
          filtered = filtered.filter(
            (transaction) => parseFloat(transaction.amount) >= parsedAmount
          );
        }
      }

      setFilteredTransactions(filtered);
    } else {
      setFilteredTransactions([]);
    }
  }, [record, sum, amount]);

  return (
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.info">
        <TextField source="phone" />
        <TextField source="email" />
        <TextField source="first_name" />
        <TextField source="middle_name" />
        <TextField source="last_name" />
        <NumberField source="balance" />
        <TextField source="region" />
        <FunctionField
          source="sex"
          label="resources.Client.fields.sex.name"
          render={(record) =>
            record.sex && t(`resources.Client.fields.sex.${record.sex}`)
          }
        />
        <FunctionField
          source="date_of_birth"
          render={(record) => {
            if (!record.date_of_birth) {
              return "";
            } else {
              return (
                record &&
                moment.unix(record.date_of_birth / 1000).format("DD-MM-YYYY")
              );
            }
          }}
          label="resources.Client.fields.date_of_birth"
        />
        <ReferenceField source="category_id" reference="Category" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="tag_ids" reference="Tag">
          <SingleFieldList linkType="show">
            <ChipField source="name" size="small" />
          </SingleFieldList>
        </ReferenceArrayField>
        <TextField source="car_number" />
        <BooleanField
          source="terms_agreement_confirm"
          sx={{
            "& .RaBooleanField-trueIcon": {
              color: "green",
            },
            "& .RaBooleanField-falseIcon": {
              color: "red",
            },
          }}
        />
        <FunctionField
          source="status"
          render={(record) =>
            t(`resources.Client.source.status.${record.status}`)
          }
        />
        <ArrayField
          source="unconfirmed_changes"
          label="resources.Client.fields.unconfirmed_changes.name"
        >
          <Datagrid optimized bulkActionButtons={null}>
            <TextField
              source="field_name"
              label="resources.Client.fields.unconfirmed_changes.field_name"
            />
            <TextField
              source="value"
              label="resources.Client.fields.unconfirmed_changes.value"
            />
            <FunctionField
              source="status"
              render={(record) =>
                t(`resources.Client.source.changes_status.${record.status}`)
              }
            />
          </Datagrid>
        </ArrayField>
        <FunctionField
          source="deleted_at"
          render={(record) => {
            if (record && record.deleted_at) {
              return <DateField source="deleted_at" />;
            } else {
              return t("");
            }
          }}
          label="resources.Client.source.deleted"
        />
        <FunctionField
          source="blocked_at"
          render={(record) => {
            if (record && record.blocked_at) {
              return <DateField source="blocked_at" />;
            } else {
              return t("");
            }
          }}
          label="resources.Client.source.blocked_at"
        />
        <DateField source="created_at" />
        <DateField source="updated_at" />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.cards">
        <ArrayField
          perPage={10}
          source="cards"
          label="resources.Client.source.infoTab.cards"
        >
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <TextField
              source="cardId"
              label="resources.Client.fields.petrolcard_cards.cardId"
            />
            <TextField
              source="serialVisible"
              label="resources.Client.fields.petrolcard_cards.serialVisible"
            />
            <BooleanField
              source="isActive"
              label="resources.Client.fields.petrolcard_cards.isActive"
            />
            <FunctionField
              source="endDate"
              label="resources.Client.fields.petrolcard_cards.endDate"
              render={(record) => moment(record.endDate).format("DD.MM.YYYY")}
            />
            <TextField
              source="cardDiscountsConf.cardDiscountsConfName"
              label="resources.Client.fields.petrolcard_cards.cardDiscountsConf.cardDiscountsConfName"
            />
            <ArrayField
              source="terminalGroups"
              label="resources.Client.fields.petrolcard_cards.terminalGroups.name"
            >
              <SingleFieldList linkType={false}>
                <ChipField
                  sx={{
                    color: isDarkMode ? "#4B0082" : "#1976d2",
                    ackgroundColor: isDarkMode && "rgba(255, 255, 255, 0.25)",
                  }}
                  size="small"
                  source="terminalGroupName"
                  label="resources.Client.fields.petrolcard_cards.terminalGroups.terminalGroupName"
                />
              </SingleFieldList>
            </ArrayField>
            <ArrayField
              source="cardIssuers"
              label="resources.Client.fields.petrolcard_cards.cardIssuers.name"
            >
              <SingleFieldList linkType={false}>
                <ChipField
                  sx={{
                    color: isDarkMode ? "#4B0082" : "#1976d2",
                    backgroundColor: isDarkMode && "rgba(255, 255, 255, 0.25)",
                  }}
                  size="small"
                  source="cardIssuersName"
                  label="resources.Client.fields.petrolcard_cards.cardIssuers.cardIssuersName"
                />
              </SingleFieldList>
            </ArrayField>
          </Datagrid>
        </ArrayField>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.transactions">
        <ArrayField
          perPage={record && record.transactions?.length}
          source="transactions"
          label="resources.Client.source.infoTab.transactions"
        >
          <>
            <DatexTransactionFilter setSum={setSum} setAmount={setAmount} />
            {sum || amount ? (
              <FilteredTransactions data={filteredTransactions} />
            ) : (
              <Datagrid
                empty={<EmptyMessage />}
                rowClick={false}
                bulkActionButtons={false}
              >
                <TextField
                  source="accountName"
                  label="resources.Client.fields.petrolcard_transactions.accountName"
                />
                <NumberField
                  source="cardId"
                  label="resources.Client.fields.petrolcard_transactions.cardId"
                />
                <TextField
                  source="serialVisible"
                  label="resources.Client.fields.petrolcard_transactions.serialVisible"
                />
                <NumberField
                  source="price"
                  label="resources.Client.fields.petrolcard_transactions.price"
                />
                <NumberField
                  source="amount"
                  label="resources.Client.fields.petrolcard_transactions.amount"
                />
                <NumberField
                  source="sum"
                  label="resources.Client.fields.petrolcard_transactions.sum"
                />
                <NumberField
                  source="politicsDiscountId"
                  label="resources.Client.fields.petrolcard_transactions.politicsDiscountId"
                />
                <NumberField
                  source="discountValue"
                  label="resources.Client.fields.petrolcard_transactions.discountValue"
                />
                <NumberField
                  source="versionId"
                  label="resources.Client.fields.petrolcard_transactions.versionId"
                />
                <NumberField
                  source="transactionId"
                  label="resources.Client.fields.petrolcard_transactions.transactionId"
                />
                <TextField
                  source="issuerName"
                  label="resources.Client.fields.petrolcard_transactions.issuerName"
                />
                <TextField
                  source="tradePointName"
                  label="resources.Client.fields.petrolcard_transactions.tradePointName"
                />
                <TextField
                  source="tradePointAddress"
                  label="resources.Client.fields.petrolcard_transactions.tradePointAddress"
                />
                <TextField
                  source="status"
                  label="resources.Client.fields.status"
                />
                <FunctionField
                  source="transactionTime"
                  label="resources.Client.fields.petrolcard_transactions.transactionTime"
                  render={(tx) =>
                    tx?.transactionTime
                      ? moment(tx.transactionTime).format("DD.MM.YYYY HH:mm:ss")
                      : ""
                  }
                />
              </Datagrid>
            )}
          </>
        </ArrayField>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="resources.Client.source.infoTab.payment_transactions">
        <ArrayField
          perPage={record && record.payment_transactions?.length}
          source="payment_transactions"
          label="resources.Client.source.infoTab.payment_transactions"
        >
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <ReferenceField
              source="merchant_payment_gateway_id"
              reference="MerchantPaymentGateway"
              link="show"
            >
              <TextField source="name" />
            </ReferenceField>
            <NumberField source="order_id" />
            <TextField source="description" />
            <FunctionField
              source="amount"
              render={(record) =>
                record.amount ? record.amount : record.amount_alt
              }
            />
            <JsonField
              source="transactions"
              reactJsonOptions={{
                name: null,
                collapsed: true,
                enableClipboard: false,
                displayDataTypes: false,
              }}
              sortable={false}
            />
            <JsonField
              source="payload"
              reactJsonOptions={{
                name: null,
                collapsed: true,
                enableClipboard: false,
                displayDataTypes: false,
              }}
              sortable={false}
            />
            <FunctionField source="status" render={(record) => record.status} />
            <DateField source="created_at" />
            <DateField source="updated_at" />
          </Datagrid>
        </ArrayField>
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  );
};

const ShowClient = () => {
  return (
    <Show title={<Title source="phone" />} actions={<ShowOnlyTopToolbar />}>
      <TabbedShowLayout
        tabs={
          <TabbedShowLayoutTabs variant="scrollable" scrollButtons={false} />
        }
      />
    </Show>
  );
};

export default ShowClient;
